/**
 * ModesRow — horizontal tablist for Workspace-V2's 5-mode switcher.
 *
 * Sprint-2 Task 2.2 (sprint.md:232). A11y per master §Step 1 +
 * sprint.md AC-8: `role="tablist"`, arrow-key navigation (Left/Right,
 * Home/End), `aria-controls` pointer to the canvas section id,
 * `aria-selected` on active tab.
 *
 * Cycle-109 sprint-204 S2.6 (2026-05-21): chip iteration driven by
 * `MODE_ORDER` (canonical-named ordering const) rather than direct
 * `MODE_REGISTRY` traversal. Behavior is identical — `MODE_REGISTRY`
 * already happens to be in canonical order — but `MODE_ORDER` is the
 * stable contract the canonical `domainModes.jsx:127` exposes, so
 * callers and tests should depend on it.
 *
 * Consumers: not yet mounted. TopChrome (Task 2.1, session 2) renders
 * this in the center of its 3-zone layout. The click handler is fed
 * upward — sprint-3 wires it to `useModeCanvasLayout(mode)` for
 * router state + localStorage persistence.
 *
 * Design: inline-styled per sprint-1 NavRail/CmdKPalette convention.
 * Uses `--cd-*` tokens exclusively; zero `--e-*` coupling.
 */

import { useRef } from 'react';
import { MODE_ORDER, getModeDef, type WorkspaceModeId } from './modeRegistry';

export interface ModesRowProps {
  /** The mode currently displayed on the canvas. */
  activeMode: WorkspaceModeId;
  /** Fires when the user selects a new mode. */
  onModeChange: (mode: WorkspaceModeId) => void;
  /** id of the canvas element the tablist controls — `aria-controls` target. */
  canvasId?: string;
  /** Optional additional class names. */
  className?: string;
}

export function ModesRow({
  activeMode,
  onModeChange,
  canvasId,
  className,
}: ModesRowProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) {
    const count = MODE_ORDER.length;
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % count;
    } else if (e.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + count) % count;
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = count - 1;
    }
    if (nextIndex !== null) {
      e.preventDefault();
      const nextMode = MODE_ORDER[nextIndex]!;
      onModeChange(nextMode);
      tabRefs.current[nextIndex]?.focus();
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Workspace modes"
      aria-orientation="horizontal"
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        fontFamily: 'var(--cd-font-sans)',
        minWidth: 0,
        flex: '1 1 auto',
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {MODE_ORDER.map((modeId, i) => {
        const mode = getModeDef(modeId)!;
        const isActive = mode.id === activeMode;
        return (
          <button
            key={mode.id}
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={canvasId}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onModeChange(mode.id)}
            onKeyDown={(e) => handleKeyDown(e, i)}
            title={`${mode.label} — ${mode.tagline}`}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'var(--cd-font-mono)',
              color: isActive ? `var(${mode.accentVar})` : 'var(--cd-fg-muted)',
              background: 'transparent',
              border: 'none',
              borderRadius: 'var(--cd-radius)',
              cursor: 'pointer',
              transition: 'color .12s',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>
              {mode.glyph}
            </span>
            <span>{mode.label}</span>
            {isActive && (
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 10,
                  right: 10,
                  bottom: -4,
                  height: 2,
                  background: `var(${mode.accentVar})`,
                  borderRadius: 1,
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
